/**
 * The translation table as rows a person reads. Every name comes from the
 * generated enums or from this bundle's schema by reflection, so a schema
 * change shows up here with no list to update.
 */
import * as flatbuffers from "flatbuffers";
import { nameEnums } from "../../flatbuffers/discovery/frameJson";
import { AddressMode } from "../../flatbuffers/generated/mce/address-mode";
import { ArgSource } from "../../flatbuffers/generated/mce/arg-source";
import { Category } from "../../flatbuffers/generated/mce/category";
import { CommandAddress } from "../../flatbuffers/generated/mce/command-address";
import { CommandRuleT } from "../../flatbuffers/generated/mce/command-rule";
import { ControlRowT } from "../../flatbuffers/generated/mce/control-row";
import { DiagnosticT } from "../../flatbuffers/generated/mce/diagnostic";
import { DiagnosticCode } from "../../flatbuffers/generated/mce/diagnostic-code";
import { EntryT } from "../../flatbuffers/generated/mce/entry";
import { EnumMapRuleT } from "../../flatbuffers/generated/mce/enum-map-rule";
import { EventRowT } from "../../flatbuffers/generated/mce/event-row";
import { FlagRuleT } from "../../flatbuffers/generated/mce/flag-rule";
import { IgnoreReason } from "../../flatbuffers/generated/mce/ignore-reason";
import { IgnoreRuleT } from "../../flatbuffers/generated/mce/ignore-rule";
import { KeyT } from "../../flatbuffers/generated/mce/key";
import { Interface } from "../../flatbuffers/generated/mce/interface";
import { Origin } from "../../flatbuffers/generated/mce/origin";
import { RecordRuleT } from "../../flatbuffers/generated/mce/record-rule";
import { OutRule } from "../../flatbuffers/generated/mce/out-rule";
import { Rule } from "../../flatbuffers/generated/mce/rule";
import { Severity } from "../../flatbuffers/generated/mce/severity";
import { SourceFile } from "../../flatbuffers/generated/mce/source-file";
import { TemplateT } from "../../flatbuffers/generated/mce/template";
import { TransformId } from "../../flatbuffers/generated/mce/transform-id";
import { TransformRuleT } from "../../flatbuffers/generated/mce/transform-rule";
import { TranslationTableT } from "../../flatbuffers/generated/mce/translation-table";
import { ValuedRuleT } from "../../flatbuffers/generated/mce/valued-rule";
import type { ReflectionSchema } from "../../flatbuffers/reflection/reflection";
import { reflectUnpack } from "../../flatbuffers/reflection/reflection-json";
import { asString } from "./messages";

const LN2_MESSAGE = "Ln2.Message";
const UNION_TYPE_SUFFIX = "_type";

/** One LN2 message a rule sends. */
export interface MceTarget {
  /** The LN2 content family, such as Status or ControllerInfo. */
  family: string;
  /** The unions between the family and the member. */
  path: string[];
  member: string;
  valueType: string | null;
  /** The label of a case in an enum map, when this target is one. */
  when?: string;
}

/** One IDL declaration and its translation. */
export interface MceMessageRow {
  key: string;
  entryKey: KeyT | null;
  category: string;
  id: number;
  subId: number;
  name: string;
  idlType: string;
  mapped: boolean;
  ruleKind: string;
  ignoreReason: string | null;
  transform: string | null;
  targets: MceTarget[];
  recordFields: { field: string; targets: MceTarget[] }[];
  keyCount: number;
  address: string;
  file: string;
  line: number;
  iface: string;
  parentCategory: string;
  profileRow: string;
  override: boolean;
}

/** One event of the events XML and its translation. */
export interface MceEventRow {
  key: string;
  eventId: number;
  text: string;
  family: string;
  member: string;
  source: string;
  override: boolean;
}

/** One MCE command a control sends. */
export interface MceCommandTarget {
  id: number;
  subId: number;
  name: string;
  address: string;
  args: string[];
}

/** One LN2 control and the MCE commands it becomes. */
export interface MceControlRow {
  key: string;
  row: ControlRowT;
  template: TemplateT | null;
  override: boolean;
  control: MceTarget | null;
  ruleKind: string;
  commands: MceCommandTarget[];
  oneShot: boolean;
  ignoreReason: string | null;
  transform: string | null;
  source: string;
  profileRow: string;
}

/** One finding of the converter. */
export interface MceDiagnosticRow {
  key: string;
  severity: string;
  isError: boolean;
  code: string;
  where: string;
  message: string;
}

/** The rows of a review. */
export interface MceTableView {
  messages: MceMessageRow[];
  events: MceEventRow[];
  controls: MceControlRow[];
  templateCount: number;
}

type Reflected = Record<string, unknown>;

function isRecord(value: unknown): value is Reflected {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Follow a reflected message's unions down to the LN2 member they name. */
function walkUnions(record: Reflected, path: string[]): Omit<MceTarget, "family" | "when"> {
  const enumMember = record.message;
  if (enumMember !== undefined && !isRecord(enumMember)) {
    const valueType = typeof record.value_type === "string" ? record.value_type : null;
    return { path, member: String(enumMember), valueType };
  }
  const unionType = Object.keys(record).find(
    (field) =>
      field.endsWith(UNION_TYPE_SUFFIX) &&
      isRecord(record[field.slice(0, -UNION_TYPE_SUFFIX.length)]),
  );
  if (!unionType) {
    return { path: path.slice(0, -1), member: path[path.length - 1] ?? "", valueType: null };
  }
  return walkUnions(record[unionType.slice(0, -UNION_TYPE_SUFFIX.length)] as Reflected, [
    ...path,
    String(record[unionType]),
  ]);
}

/** Member 0 of a leaf such as NormalStatus: the name its `message` field has when unset. */
function zeroMemberName(schema: ReflectionSchema, variant: string): string | null {
  if (!/^(Normal|Valued)/.test(variant)) return null;
  const leaf = schema.objects().find((object) => object.name().endsWith(`.${variant}`));
  const field = leaf?.fields().find((one) => one.name() === "message");
  const index = field?.type()?.index() ?? -1;
  const values = index >= 0 ? schema.enums()[index]?.values() : [];
  return values?.find((value) => Number(value.value()) === 0)?.name() ?? null;
}

/** The LN2 message a template sends, read from its packed bytes by reflection. */
export function describeTemplate(schema: ReflectionSchema, template: TemplateT): MceTarget | null {
  const message = template.msg?.msg;
  if (!message) return null;
  const builder = new flatbuffers.Builder(256);
  builder.finish(message.pack(builder));
  const root = schema.objects().find((object) => object.name() === LN2_MESSAGE);
  const record = reflectUnpack(schema, LN2_MESSAGE, builder.asUint8Array());
  if (!root || !record) return null;
  const named = nameEnums(schema, root, record);
  const content = named.content as Reflected | undefined;
  if (!content) return null;
  const walked = walkUnions(content, []);
  const leaf = zeroMemberName(schema, walked.member);
  if (leaf) {
    // Member 0 is the default, so the leaf table holds no `message` and the walk stopped a level short.
    walked.path = [...walked.path, walked.member];
    walked.member = leaf;
  }
  const [familyPath, ...path] = walked.path;
  // The first union names its variant Family_FamilyMessage.
  const family = (familyPath ?? walked.member).split("_")[0];
  return { ...walked, family, path };
}

interface RuleSummary {
  kind: string;
  ignoreReason: string | null;
  transform: string | null;
  targets: MceTarget[];
  recordFields: { field: string; targets: MceTarget[] }[];
}

function summarizeRule(
  ruleType: Rule,
  rule: unknown,
  targetOf: (index: number) => MceTarget | null,
): RuleSummary {
  const targets = (indexes: number[]) =>
    indexes.map(targetOf).filter((target): target is MceTarget => target !== null);
  const summary: RuleSummary = {
    kind: Rule[ruleType] ?? "",
    ignoreReason: null,
    transform: null,
    targets: [],
    recordFields: [],
  };
  if (rule instanceof IgnoreRuleT) summary.ignoreReason = IgnoreReason[rule.reason] ?? "";
  if (rule instanceof FlagRuleT || rule instanceof ValuedRuleT) {
    summary.targets = targets([rule.template]);
  }
  if (rule instanceof EnumMapRuleT) {
    summary.targets = rule.cases.flatMap((armOfMap) => {
      const target = targetOf(rule.set[armOfMap.member]);
      return target ? [{ ...target, when: armOfMap.values.join(", ") }] : [];
    });
  }
  if (rule instanceof TransformRuleT) {
    summary.transform = TransformId[rule.id] ?? "";
    summary.targets = targets(rule.outputs);
  }
  if (rule instanceof RecordRuleT) {
    summary.recordFields = rule.fields.map((field) => ({
      field: asString(field.field),
      targets: summarizeRule(field.ruleType, field.rule, targetOf).targets,
    }));
  }
  return summary;
}

/** The name of a source file in a table: its entries record the kind and the include. */
function sourceName(entry: EntryT, fileNames: MceSourceNames): string {
  if (entry.source === SourceFile.Include) return fileNames.includes[entry.includeIndex] ?? "";
  if (entry.source === SourceFile.Controller) return fileNames.controller;
  return fileNames.main;
}

/** The names of a bundle file by the part it plays. */
export interface MceSourceNames {
  main: string;
  controller: string;
  includes: string[];
  eventsXml: string;
}

function messageRow(
  entry: EntryT,
  targetOf: (index: number) => MceTarget | null,
  fileNames: MceSourceNames,
): MceMessageRow {
  const key = entry.key;
  const rule = summarizeRule(entry.ruleType, entry.rule, targetOf);
  const category = Category[key?.category ?? Category.Parameter] ?? "";
  const iface = Interface[key?.iface ?? Interface.Dispatcher] ?? "";
  return {
    key: `${iface}-${category}-${key?.id}-${key?.subId}`,
    entryKey: key,
    category,
    id: key?.id ?? 0,
    subId: key?.subId ?? 0,
    name: asString(entry.name),
    idlType: asString(entry.dataType),
    mapped: entry.origin === Origin.Profile || entry.origin === Origin.Override,
    ruleKind: rule.kind,
    ignoreReason: rule.ignoreReason,
    transform: rule.transform,
    targets: rule.targets,
    recordFields: rule.recordFields,
    keyCount: Math.max(entry.arrayLen, 1),
    address: AddressMode[entry.address] ?? "",
    file: sourceName(entry, fileNames),
    line: entry.line,
    iface,
    parentCategory: asString(entry.parentCategory),
    profileRow: asString(entry.profileRow),
    override: entry.origin === Origin.Override,
  };
}

function eventRow(
  event: EventRowT,
  targetOf: (index: number) => MceTarget | null,
  index: number,
): MceEventRow {
  const rule = summarizeRule(event.ruleType, event.rule, targetOf);
  const target = rule.targets[0];
  return {
    key: `${event.key?.types}-${event.key?.eventId}-${index}`,
    eventId: event.key?.eventId ?? 0,
    text: asString(event.text),
    family: target?.family ?? rule.kind,
    member: target?.member ?? rule.transform ?? rule.ignoreReason ?? "",
    source: Origin[event.origin] ?? "",
    override: event.origin === Origin.Override,
  };
}

function controlRow(
  control: ControlRowT,
  index: number,
  targetOf: (index: number) => MceTarget | null,
  commandName: (id: number, subId: number) => string,
  templates: TemplateT[],
): MceControlRow {
  const { rule } = control;
  const commands =
    rule instanceof CommandRuleT
      ? rule.commands.map((command) => ({
          id: command.key?.id ?? 0,
          subId: command.key?.subId ?? 0,
          name: commandName(command.key?.id ?? 0, command.key?.subId ?? 0),
          address: CommandAddress[command.address] ?? "",
          args: command.args.map((arg) => `${asString(arg.name)}: ${ArgSource[arg.source] ?? ""}`),
        }))
      : [];
  return {
    key: `${control.match}-${index}`,
    row: control,
    template: templates[control.match] ?? null,
    override: control.origin === Origin.Override,
    control: targetOf(control.match),
    ruleKind: OutRule[control.ruleType] ?? "",
    commands,
    oneShot: rule instanceof CommandRuleT && rule.oneShot,
    ignoreReason: rule instanceof IgnoreRuleT ? (IgnoreReason[rule.reason] ?? "") : null,
    transform: rule instanceof TransformRuleT ? (TransformId[rule.id] ?? "") : null,
    source: Origin[control.origin] ?? "",
    profileRow: asString(control.profileRow),
  };
}

/** Everything a review shows. Rows the IDL does not declare (ProfileOnly) are left out. */
export function buildTableView(
  table: TranslationTableT,
  schema: ReflectionSchema,
  fileNames: MceSourceNames,
): MceTableView {
  const described = new Map<number, MceTarget | null>();
  const targetOf = (index: number) => {
    if (!described.has(index)) {
      const template = table.templates[index];
      described.set(index, template ? describeTemplate(schema, template) : null);
    }
    return described.get(index) ?? null;
  };
  const commandNames = new Map(
    table.entries
      .filter((entry) => entry.key?.category === Category.Command)
      .map((entry) => [`${entry.key?.id}/${entry.key?.subId}`, asString(entry.name)]),
  );
  const commandName = (id: number, subId: number) => commandNames.get(`${id}/${subId}`) ?? "";
  return {
    messages: table.entries
      .filter((entry) => entry.origin !== Origin.ProfileOnly)
      .map((entry) => messageRow(entry, targetOf, fileNames)),
    events: table.events.map((event, index) => eventRow(event, targetOf, index)),
    controls: table.controls.map((control, index) =>
      controlRow(control, index, targetOf, commandName, table.templates),
    ),
    templateCount: table.templates.length,
  };
}

/** Findings of a validate or build, with their file, line and column. */
export function diagnosticRows(
  diagnostics: DiagnosticT[],
  fileNames: MceSourceNames,
): MceDiagnosticRow[] {
  const fileOf = (diagnostic: DiagnosticT): string => {
    switch (diagnostic.source) {
      case SourceFile.Main:
        return fileNames.main;
      case SourceFile.Controller:
        return fileNames.controller;
      case SourceFile.EventsXml:
        return fileNames.eventsXml;
      case SourceFile.Include:
        return fileNames.includes[diagnostic.includeIndex] ?? "";
      default:
        return "";
    }
  };
  // Errors first: a page of warnings must not hide why an upload was refused.
  const worstFirst = [...diagnostics].sort((a, b) => a.severity - b.severity);
  return worstFirst.map((diagnostic, index) => {
    const file = fileOf(diagnostic);
    const position = diagnostic.line > 0 ? `:${diagnostic.line}:${diagnostic.column}` : "";
    return {
      key: `${diagnostic.code}-${index}`,
      severity: Severity[diagnostic.severity] ?? "",
      isError: diagnostic.severity === Severity.Error,
      code: DiagnosticCode[diagnostic.code] ?? String(diagnostic.code),
      where: file ? `${file}${position}` : "",
      message: asString(diagnostic.message),
    };
  });
}
